import React, { ChangeEvent } from 'react';
import { TeacherApplicationForm } from '../../model/TeacherApplicationForm';
import {
  validateEmail,
  validateItalianPhone,
  validateProvince,
  validateCity,
  validateStreet,
  validateZip,
} from '../../utils/teacherApplicationValidators';
import { InputField } from '../InputField';
import { Province, City } from '../../model/TeacherInfo';

interface PersonalInfoStepProps {
  formData: TeacherApplicationForm;
  setFormData: (data: TeacherApplicationForm) => void;
  errors: { [key: string]: string };
  setErrors: (errors: { [key: string]: string }) => void;
  provinces: Province[];
  cities: City[];
}

const PersonalInfoStep: React.FC<PersonalInfoStepProps> = ({
  formData,
  setFormData,
  errors,
  setErrors,
  provinces,
  cities,
}) => {
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Reset city when province changes
    if (name === 'province_id') {
      setFormData({ ...formData, province_id: Number(value), city_id: 0 });
    } else if (name === 'city_id') {
      setFormData({ ...formData, city_id: Number(value) });
    } else {
      setFormData({ ...formData, [name]: value });
    }

    // Real-time validation
    let error = '';
    switch (name) {
      case 'email':
        error = validateEmail(value);
        break;
      case 'phone_number':
        error = validateItalianPhone(value);
        break;
      case 'province_id':
        error = validateProvince(Number(value));
        break;
      case 'city_id':
        error = validateCity(Number(value));
        break;
      case 'street_name':
        error = validateStreet(value);
        break;
      case 'zip':
        error = validateZip(value);
        break;
    }

    setErrors({ ...errors, [name]: error });
  };

  return (
    <div className="w-full p-1">
      <div className="grid grid-cols-1 md:grid-cols-2 md:gap-4">
        <InputField
          id="first_name"
          name="first_name"
          type="text"
          placeholder="Inserisci il tuo nome"
          value={formData.first_name}
          onChange={handleChange}
          required
          error={errors.first_name}
          autocomplete="given-name"
        />

        <InputField
          id="last_name"
          name="last_name"
          type="text"
          placeholder="Inserisci il tuo cognome"
          value={formData.last_name}
          onChange={handleChange}
          required
          error={errors.last_name}
          autocomplete="family-name"
        />
      </div>

      <InputField
        id="email"
        name="email"
        type="email"
        placeholder="Inserisci la tua email"
        value={formData.email}
        onChange={handleChange}
        required
        error={errors.email}
        autocomplete="email"
      />

      <div>
        <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700">
          Telefono
        </label>
        <div className="flex gap-2">
          <span className="border border-gray-300 rounded-lg px-4 py-2 bg-gray-50 text-gray-600">+39</span>
          <input
            id="phone_number"
            name="phone_number"
            type="tel"
            placeholder="3XXXXXXXXX"
            value={formData.phone_number}
            onChange={handleChange}
            onKeyDown={(e) => {
              if (!/^\d$/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
                e.preventDefault();
              }
            }}
            maxLength={10}
            className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-fpcred focus:border-transparent ${
              errors.phone_number ? 'border-red-500' : 'border-gray-300'
            }`}
            autoComplete="tel"
          />
        </div>
        {errors.phone_number && <p className="text-sm text-red-500">{errors.phone_number}</p>}
      </div>

      {/* Dati Domicilio */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label htmlFor="province_id" className="block text-sm font-medium text-gray-700">
            Provincia
          </label>
          <select
            id="province_id"
            name="province_id"
            value={formData.province_id}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-fpcred focus:border-transparent ${
              errors.province_id ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value={0} disabled>
              Seleziona provincia
            </option>
            {provinces.map((province) => (
              <option key={province.id} value={province.id}>
                {province.name}
              </option>
            ))}
          </select>
          {errors.province_id && <p className="text-sm text-red-500">{errors.province_id}</p>}
        </div>

        <div>
          <label htmlFor="city_id" className="block text-sm font-medium text-gray-700">
            Città
          </label>
          <select
            id="city_id"
            name="city_id"
            value={formData.city_id}
            onChange={handleChange}
            disabled={cities.length === 0}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-fpcred focus:border-transparent ${
              errors.city_id ? 'border-red-500' : 'border-gray-300'
            } ${cities.length === 0 ? 'bg-gray-100' : ''}`}
          >
            <option value={0} disabled>
              Seleziona città
            </option>
            {cities.map((city) => (
              <option key={city.id} value={city.id}>
                {city.name}
              </option>
            ))}
          </select>
          {errors.city_id && <p className="text-sm text-red-500">{errors.city_id}</p>}
        </div>

        <div>
          <label htmlFor="zip" className="block text-sm font-medium text-gray-700">
            CAP
          </label>
          <input
            id="zip"
            name="zip"
            type="text"
            placeholder="00000"
            value={formData.zip}
            onChange={handleChange}
            onKeyDown={(e) => {
              if (
                !/^\d$/.test(e.key) &&
                !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)
              ) {
                e.preventDefault();
              }
            }}
            maxLength={5}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-fpcred focus:border-transparent ${
              errors.zip ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.zip && <p className="text-sm text-red-500">{errors.zip}</p>}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <div className="col-span-3">
          <label htmlFor="street_name" className="block text-sm font-medium text-gray-700">
            Via
          </label>
          <input
            id="street_name"
            name="street_name"
            type="text"
            placeholder="Via, nome strada"
            value={formData.street_name}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-fpcred focus:border-transparent ${
              errors.street_name ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.street_name && <p className="text-sm text-red-500">{errors.street_name}</p>}
        </div>

        <div className="col-span-1">
          <label htmlFor="street_number" className="block text-sm font-medium text-gray-700">
            Numero
          </label>
          <input
            id="street_number"
            name="street_number"
            type="text"
            placeholder="N."
            value={formData.street_number}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fpcred focus:border-transparent"
          />
        </div>
      </div>
    </div>
  );
};

export default PersonalInfoStep;
